import React from "react";
import { Box } from "@mui/material";
import { DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import ManagementReportPage from "@common/components/managementReport/ManagementReportPage";
import ManagementReportDescription from "@common/components/managementReport/managementReportDescription/ManagementReportDescription";
import ManagementReportCharts from "@common/components/managementReport/managementReportCharts/ManagementReportCharts";
import { useTranslation } from "@localization/useTranslation";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { LegalEntityAuditStatus } from "../../legalEntity/legalEntityAuditTab/LegalEntityAudit.types";
import useInternalAssessmentGroupManagementReport from "./InternalAssessmentGroupManagementReport.hooks";
import ManagementReportSummary from "@common/components/managementReport/managementReportSummary/ManagementReportSummary";
import InternalAssessmentReportCompletedAssessments from "./internalAssessmentReportCompletedAssessments/InternalAssessmentReportCompletedAssessments";
import InternalAssessmentReportInProgressAssessments from "./internalAssessmentReportInProgressAssessments/InternalAssessmentReportInProgressAssessments";
import InternalAssessmentReportExcludedAssessments from "./internalAssessmentReportExcludedAssessments/InternalAssessmentReportExcludedAssessments";

const inProgressStatuses = [LegalEntityAuditStatus.InProgress, LegalEntityAuditStatus.Other, LegalEntityAuditStatus.Opened, LegalEntityAuditStatus.Sent];

function InternalAssessmentGroupManagementReport() {
    const { translateString } = useTranslation();
    const { customerName } = useUserContext();
    const { isLoading, data, isPdf, chartData } = useInternalAssessmentGroupManagementReport();

    const assessments = data?.assessments.rows ?? [];

    return (
        <ManagementReportPage
            isLoading={isLoading || data === undefined}
            isPdf={isPdf}
            groupName={data?.name ?? ""}
            sender={data?.sender ?? customerName}
            deadline={data?.deadline}
        >
            {data !== undefined && (
                <>
                    <ManagementReportDescription scoreEnabled={data.scoreEnabled} auditName={data.name} variant="internalAssessment" />

                    <ManagementReportSummary
                        isPdf={isPdf}
                        sent={data.sent}
                        deadline={data.deadline}
                        progress={data.progress}
                        templateId={data.assessmentTemplateId}
                        templateName={data.assessmentTemplate}
                        templateLabelKey="assessmentTemplate"
                        respondentLabelKey="recipients"
                        respondentCount={data.numberOfAssignees}
                        openedCount={data.openedCount}
                        submittedCount={data.submittedCount}
                        excludedCount={data.excludedCount}
                        responsible={data.responsible}
                        sender={data.sender}
                        senderId={data.senderId}
                        scoreEnabled={data.scoreEnabled}
                        overallRiskLevel={data.overallRiskLevel}
                        overallRiskLevelName={data.overallRiskLevelName}
                        overallRiskScore={data.overallRiskScore}
                    />

                    {chartData !== undefined && (
                        <ManagementReportCharts
                            isPdf={isPdf}
                            data={{
                                averageRiskLevel: data.overallRiskLevel,
                                averageScore: data.overallRiskScore,
                                averageScoreName: data.overallRiskLevelName,
                                averageStatus: LegalEntityAuditStatus.Submitted,
                            }}
                            intervalScoreChartData={chartData}
                        />
                    )}

                    <Box className="page-break-before">
                        <DotLegalHeader marginTop={isPdf ? 1 : 4} marginBottom={0} headerStyle="xxxl">
                            {translateString("overviewOfIndividualRespondents")}
                        </DotLegalHeader>

                        <InternalAssessmentReportCompletedAssessments
                            isLoading={isLoading}
                            isPdf={isPdf}
                            assessments={assessments.filter((assessment) => !assessment.isClosed && assessment.status === LegalEntityAuditStatus.Submitted)}
                        />

                        <InternalAssessmentReportInProgressAssessments
                            isLoading={isLoading}
                            isPdf={isPdf}
                            assessments={assessments.filter((assessment) => !assessment.isClosed && inProgressStatuses.includes(assessment.status))}
                        />

                        <InternalAssessmentReportExcludedAssessments
                            isLoading={isLoading}
                            isPdf={isPdf}
                            assessments={assessments.filter((assessment) => assessment.isClosed)}
                        />
                    </Box>
                </>
            )}
        </ManagementReportPage>
    );
}

export default InternalAssessmentGroupManagementReport;
